import React, { useEffect, useMemo, useState, useCallback } from 'react';

/**
 * ClanSubnav
 * Barra sticky de navegación interna por anclas con scroll suave y resalte de sección activa.
 * Secciones soportadas (ids en la página): resumen, prestigio, recursos, infra, territorios, tecnicas, lideres
 *
 * Props:
 * - sections?: string[] (ids en orden). Default: lista por defecto.
 * - offset?: number (px restados al hacer scroll por cabeceras sticky). Default: 80.
 */
export default function ClanSubnav({ sections, offset = 80 }) {
  const items = useMemo(
    () =>
      Array.isArray(sections) && sections.length
        ? sections
        : ['resumen', 'prestigio', 'recursos', 'infra', 'territorios', 'tecnicas', 'lideres'],
    [sections]
  );

  const [active, setActive] = useState(items[0]);

  // Scroll suave a ancla respetando offset
  const scrollToId = useCallback(
    (id) => (e) => {
      e?.preventDefault?.();
      const el = document.getElementById(id);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = window.scrollY + rect.top - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      // Marcar activo inmediatamente para feedback
      setActive(id);
      // Actualizar hash sin salto brusco (evitar 'history' global para ESLint)
      if (typeof window !== 'undefined' && window.history && typeof window.history.pushState === 'function') {
        try {
          window.history.pushState(null, '', `#${id}`);
        } catch {
          // fallback a hash normal si el navegador bloquea pushState
          window.location.hash = `#${id}`;
        }
      } else if (typeof window !== 'undefined') {
        window.location.hash = `#${id}`;
      }
    },
    [offset]
  );

  // Scrollspy básico con IntersectionObserver
  useEffect(() => {
    const els = items
      .map((id) => {
        const el = document.getElementById(id);
        return el ? { id, el } : null;
      })
      .filter(Boolean);

    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        // Elegimos la sección más visible en viewport
        let best = { id: active, ratio: 0 };
        entries.forEach((en) => {
          const id = en.target.getAttribute('id');
          const ratio = en.intersectionRatio || 0;
          if (ratio > best.ratio) best = { id, ratio };
        });
        if (best.id && best.id !== active) setActive(best.id);
      },
      {
        root: null,
        rootMargin: `-${offset + 1}px 0px -60% 0px`,
        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
      }
    );

    els.forEach(({ el }) => io.observe(el));
    return () => io.disconnect();
  }, [items, offset, active]);

  // Marcar activo desde hash inicial si existe
  useEffect(() => {
    const id = (window.location.hash || '').replace(/^#/, '');
    if (id && items.includes(id)) setActive(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <nav className="clan-subnav" aria-label="Secciones del clan">
      <ul className="clan-subnav-list">
        {items.map((id) => (
          <li key={id} className="clan-subnav-item">
            <a
              href={`#${id}`}
              onClick={scrollToId(id)}
              className={`clan-subnav-link ${active === id ? 'is-active' : ''}`}
              aria-current={active === id ? 'page' : undefined}
            >
              {labelFor(id)}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function labelFor(id) {
  switch (id) {
    case 'resumen':
      return 'Resumen';
    case 'prestigio':
      return 'Prestigio';
    case 'recursos':
      return 'Recursos';
    case 'infra':
      return 'Infraestructura';
    case 'territorios':
      return 'Territorios';
    case 'tecnicas':
      return 'Técnicas';
    case 'lideres':
      return 'Líderes';
    default:
      return id;
  }
}